/**
 * VSL ao vivo (VTurb, `GET /api/painel/vsl`): curva de retenção, KPIs da VSL, origem das sessões por
 * utm_content e "assistindo agora". O servidor fala com a API de Analytics da VTurb e manda as taxas
 * já como fração 0..1. Sem a chave da VTurb, a página VSL mostra "VTurb não conectada"; com a chave e
 * a leitura falhando, "VTurb com erro". Nos dois casos nada vem da simulação: sem curva, sem origem e
 * sem os KPIs da VSL (o núcleo tira do snapshot o que não foi calculado de verdade).
 */

import type {
  DashboardSnapshot,
  IntegrationStatus,
  KpiId,
  KpiIdV2,
  KpiValue,
  KpiValueV2,
  ListQuery,
  Ms,
  Page,
  PainelProviderV2,
  Query,
  RetentionCurve,
  RetentionPoint,
  RetentionQuery,
  Settings,
  VslSourceRow,
} from '@rota/contracts';
import { getWithRetry } from './http';
import {
  dayStart,
  isPaid,
  kv,
  matches,
  paginate,
  type RealCtx,
  type RealKpiInput,
  type RealKpisResult,
  type RealModule,
  type RealOrderRow,
  rangeMs,
  todayKey,
} from './realCore';

export interface RealVslStats {
  views: number;
  plays: number;
  finished: number;
  clicks: number;
  overPitch: number;
  underPitch: number;
  playRate: number | null;
  engagement: number | null;
  pitchRetention: number | null;
  conversions: number;
  conversionRate: number | null;
  amountBrlCents: number;
}
export interface RealVslPlayer {
  playerId: string;
  name: string;
  durationSec: number;
  pitchSec: number;
  stats: RealVslStats;
  curve: { second: number; reached: number }[];
  totalPlays: number;
  averageWatchedSec: number | null;
  sources: (RealVslStats & { key: string })[];
}
export interface RealVsl {
  configured: boolean;
  generatedAt: number;
  range: { from: number; to: number };
  main: RealVslPlayer | null;
  upsell: RealVslPlayer | null;
  live: number | null;
  error: string | null;
}

/**
 * Relatório da VTurb no formato do servidor quando a leitura falhou: "ligada, mas com erro" (o rodapé
 * e o portão dizem "VTurb com erro"), nunca "não configurada" (isso é só `configured: false`).
 */
export function vslFailure(from: number, to: number, message: string, at: number = Date.now()): RealVsl {
  return {
    configured: true,
    generatedAt: at,
    range: { from, to },
    main: null,
    upsell: null,
    live: null,
    error: message,
  };
}

export async function fetchRealVsl(from: number, to: number): Promise<RealVsl | null> {
  const r = await getWithRetry(`/api/painel/vsl?from=${from}&to=${to}`);
  if (!r.ok) return vslFailure(from, to, `HTTP ${r.status}`);
  return (await r.json()) as RealVsl;
}

/** Situação da VTurb no rodapé e no portão da página VSL. */
export function vturbStatus(
  v: RealVsl | null,
  sim: IntegrationStatus | undefined,
  now: Ms,
): IntegrationStatus {
  const base = {
    ...(sim ?? {}),
    source: 'vturb' as const,
    label: 'VTurb · API de Analytics',
    latency: 'near_realtime' as const,
    slaMs: sim?.slaMs ?? 10 * 60_000,
  };
  // Falha ao buscar /api/painel/vsl: erro (quem chama mantém a última leitura), nunca "não conectada".
  if (v == null)
    return {
      ...base,
      state: 'error',
      lastSyncAt: null,
      lagMs: null,
      eventsLastHour: null,
      lastError: { code: 'vturb', message: 'O servidor não respondeu', at: now },
    } as IntegrationStatus;
  if (!v.configured)
    return {
      ...base,
      state: 'not_configured',
      lastSyncAt: null,
      lagMs: null,
      eventsLastHour: null,
    } as IntegrationStatus;
  if (v.error || !v.main)
    return {
      ...base,
      state: 'error',
      lastSyncAt: null,
      lagMs: null,
      eventsLastHour: null,
      ...(v.error ? { lastError: { code: 'vturb', message: v.error, at: v.generatedAt } } : {}),
    } as IntegrationStatus;
  return {
    ...base,
    state: 'connected',
    lastSyncAt: v.generatedAt,
    lagMs: Math.max(0, now - v.generatedAt),
    eventsLastHour: null,
  } as IntegrationStatus;
}

/** Curva no formato do Painel: fração dos plays que ainda assistia em cada segundo (passos de 5 s). */
export function toCurve(p: RealVslPlayer, now: Ms): RetentionCurve {
  const total = p.totalPlays;
  const points: RetentionPoint[] = p.curve.map((c) => ({
    second: c.second,
    reached: c.reached,
    pct: total > 0 ? c.reached / total : 0,
  }));
  const atPitch = points.find((x) => x.second >= p.pitchSec);
  return {
    playerId: p.playerId,
    stepSec: 5,
    totalPlays: total,
    pitchSec: p.pitchSec,
    pitchReachedPct: p.stats.pitchRetention ?? atPitch?.pct ?? 0,
    segment: { key: 'all', value: 'all', label: 'Todos' },
    points,
    freshness: [{ source: 'vturb', latency: 'near_realtime', fetchedAt: now, dataAsOf: now, stale: false }],
  };
}

/** Marcos da retenção (30 s, 1 min, 3 min, 5 min, 10 min e a oferta). */
export function milestones(curve: RetentionCurve): { second: number; pct: number; delta: number | null }[] {
  const at = (s: number) => {
    const p = curve.points.find((x) => x.second >= s);
    return p ? p.pct : 0;
  };
  const marks = [30, 60, 180, 300, 600];
  if (curve.pitchSec > 0) marks.push(curve.pitchSec);
  return [...new Set(marks)]
    .filter((s) => s <= (curve.points[curve.points.length - 1]?.second ?? 0))
    .sort((a, b) => a - b)
    .map((second) => ({ second, pct: at(second), delta: null }));
}

/** Nome legível de um utm_content (automação, bio, ID ou nome do anúncio). */
export function sourceLabel(key: string, adNames: Map<string, string>): string {
  const k = key.toLowerCase();
  if (k.startsWith('automacao')) return 'Automação do direct';
  if (k.includes('bio')) return 'Bio do Instagram';
  if (k.startsWith('ultimo_')) return sourceLabel(key.slice(7), adNames);
  const id = /^\d{10,}$/.test(key) ? key : (key.split('|')[1] ?? null);
  if (id && adNames.has(id)) return `Anúncio · ${adNames.get(id)}`;
  return key.split('|')[0] ?? key;
}

export function toSourceRows(p: RealVslPlayer, adNames: Map<string, string>): VslSourceRow[] {
  return p.sources.map((s) => ({
    key: s.key,
    label: sourceLabel(s.key, adNames),
    ...(/^\d{10,}$/.test(s.key) ? { adId: s.key } : {}),
    plays: s.plays,
    playRate: s.playRate ?? 0,
    pitchRetention: s.pitchRetention ?? 0,
    postPitchCtr: null,
    conv: s.conversionRate,
    rpv: s.plays > 0 && s.amountBrlCents > 0 ? Math.round(s.amountBrlCents / s.plays) : null,
    status_: {},
  }));
}

/** Quantos chegaram a um segundo do vídeo (para o funil: 1º minuto e oferta). */
export function reachedAt(p: RealVslPlayer, second: number): number | null {
  const c = p.curve.find((x) => x.second >= second);
  return c ? c.reached : null;
}

/** Fração da curva que passou de um segundo (sessões que chegaram ÷ sessões do início). */
export function curveRate(p: RealVslPlayer, second: number): number | null {
  const start = p.curve[0]?.reached ?? 0;
  const at = reachedAt(p, second);
  return start > 0 && at != null ? Math.min(1, at / start) : null;
}

/**
 * Etapas da VSL no funil, todas na mesma base: o play e o pitch vêm por aparelho (como a VTurb mostra
 * no painel dela) e a curva vem por sessão, então o minuto 1 é a fração da curva aplicada aos plays.
 * `outOfOrder`: a VTurb mandou números que não fecham (minuto 1 acima do play ou abaixo do pitch); a
 * tela avisa em vez de esconder.
 */
export function vslStageCounts(p: RealVslPlayer): {
  views: number;
  plays: number;
  minute1: number | null;
  pitch: number;
  outOfOrder: boolean;
} {
  const plays = p.stats.plays;
  const pitch = p.stats.overPitch;
  const share = curveRate(p, 60);
  const raw = share == null ? null : Math.round(plays * share);
  const outOfOrder = pitch > plays || (raw != null && (raw > plays || raw < pitch));
  return {
    views: p.stats.views,
    plays,
    minute1: raw == null ? null : Math.min(raw, plays),
    pitch,
    outOfOrder,
  };
}

// ---------- módulo do modo ao vivo ----------

/** Relatório da VTurb com cache curto; o último fica para o stream ("assistindo agora"). */
export function createVslSource(clock: () => number, fetchVsl: typeof fetchRealVsl = fetchRealVsl) {
  const cache = new Map<string, { at: number; p: Promise<RealVsl | null> }>();
  let last: RealVsl | null = null;
  const load = (from: number, to: number): Promise<RealVsl | null> => {
    const key = `${from}|${Math.floor(to / 60_000)}`;
    const hit = cache.get(key);
    if (hit && clock() - hit.at < 60_000) return hit.p;
    const p = fetchVsl(from, to)
      // Rede fora ou resposta quebrada: "VTurb com erro" (nunca "não configurada").
      .catch((e: unknown) =>
        vslFailure(from, to, e instanceof Error ? e.message : 'VTurb fora do ar', clock()),
      )
      .then((v) => {
        if (v?.configured) last = v;
        return v;
      });
    cache.set(key, { at: clock(), p });
    return p;
  };
  return { load, last: () => last };
}

/** KPIs da VSL: só com a chave da VTurb salva; sem ela, a página VSL mostra "VTurb não conectada". */
export const VSL_KPI_IDS = [
  'vslViews',
  'vslPlayRate',
  'vslEngagement',
  'pitchRetention',
  'vslPageConv',
  'vslLive',
] as const satisfies readonly KpiId[];

/** KPIs da V2 que só a VTurb mede: minuto 1, conversão no pitch e o pitch do vídeo do 20X. */
export const VSL_KPI_V2_IDS = [
  'vslMinute1',
  'pitchConversion',
  'upsellPitchRetention',
] as const satisfies readonly KpiIdV2[];
type VslKpiV2Id = (typeof VSL_KPI_V2_IDS)[number];

/** Abaixo disso o pitch do vídeo do 20X vira "amostra pequena" (o mesmo mínimo da Pauta). */
export const MIN_UPSELL_PLAYS = 30;

/** Fonte fora (VTurb sem chave ou com erro): a tela mostra o aviso da VTurb, nunca a curva simulada. */
const vturbUnavailable = (message: string): Error & { code: 'source_unavailable' } =>
  Object.assign(new Error(message), { code: 'source_unavailable' as const });

/** VTurb ligada e respondendo (com erro ou sem chave, null). */
const vslOk = (v: RealVsl | null): RealVsl | null => (v?.configured && !v.error ? v : null);

/**
 * Player do boot vindo da VTurb (fora do contrato): a tela usa a duração e o pitch reais e só fala em
 * "N blocos da copy" quando o dono salvou os blocos nos Ajustes. `pitchKnown: false` = a VTurb não
 * tem o momento do pitch e o dono não salvou um nos Ajustes: a tela não fala do pitch.
 */
export interface LiveVslPlayerInfo {
  source?: 'vturb';
  blocksSaved?: boolean;
  pitchKnown?: boolean;
}

/** O que o dono salvou da VSL nos Ajustes › Economia (null = não salvou). */
export interface SavedVsl {
  /** Os blocos da copy foram salvos (sem isso, são os padrões). */
  blocks: boolean;
  /** Momento do pitch da VSL principal salvo nos Ajustes. */
  pitchSec: number | null;
  /** Momento do pitch do vídeo do 20X salvo nos Ajustes. */
  upsellPitchSec: number | null;
}

const NOTHING_SAVED: SavedVsl = { blocks: false, pitchSec: null, upsellPitchSec: null };
const secOrNull = (x: unknown): number | null => (typeof x === 'number' && x > 0 ? x : null);

/** O que o dono salvou da VSL nos Ajustes (blocos da copy e os momentos do pitch). */
export async function fetchSavedVsl(): Promise<SavedVsl> {
  try {
    const r = await getWithRetry('/api/painel/ajustes');
    if (!r.ok) return NOTHING_SAVED;
    const j = (await r.json()) as {
      settings?: {
        economicsV2?: {
          vsl?: { blocks?: unknown; pitchSec?: unknown };
          upsellVsl?: { pitchSec?: unknown };
        };
      } | null;
    };
    const e = j.settings?.economicsV2;
    const blocks = e?.vsl?.blocks;
    return {
      blocks: Array.isArray(blocks) && blocks.length > 0,
      pitchSec: secOrNull(e?.vsl?.pitchSec),
      upsellPitchSec: secOrNull(e?.upsellVsl?.pitchSec),
    };
  } catch {
    return NOTHING_SAVED;
  }
}

/**
 * Os 3 números da V2 que a VTurb mede, no período: minuto 1 pela curva do player principal, conversão
 * no pitch = vendas do curso ÷ quem chegou ao pitch (estimada: o clique ainda não é evento) e o pitch
 * do vídeo do 20X (com amostra mínima). Sem o player, o número fica de fora (a tela diz "não medido").
 */
export function vslV2Values(
  main: RealVslPlayer | null,
  upsell: RealVslPlayer | null,
  frontSales: number,
): Partial<Record<VslKpiV2Id, Pick<KpiValueV2, 'value' | 'status' | 'sample'>>> {
  const out: Partial<Record<VslKpiV2Id, Pick<KpiValueV2, 'value' | 'status' | 'sample'>>> = {};
  if (main) {
    out.vslMinute1 = { value: curveRate(main, 60), status: 'neutral' };
    const pitch = main.stats.overPitch;
    out.pitchConversion = { value: pitch > 0 ? frontSales / pitch : null, status: 'neutral' };
  }
  if (upsell) {
    const plays = upsell.totalPlays;
    const rate = upsell.stats.pitchRetention ?? (plays > 0 ? curveRate(upsell, upsell.pitchSec) : null);
    out.upsellPitchRetention =
      plays >= MIN_UPSELL_PLAYS
        ? { value: rate, status: 'neutral' }
        : {
            value: plays > 0 ? rate : null,
            status: 'insufficient',
            sample: { n: plays, min: MIN_UPSELL_PLAYS, unit: 'plays' },
          };
  }
  return out;
}

export function vslModule(ctx: RealCtx, savedVsl: () => Promise<SavedVsl> = fetchSavedVsl): RealModule {
  const { clock } = ctx;

  const vfreshOf = (now: number, v: RealVsl) => [
    {
      source: 'vturb' as const,
      latency: 'near_realtime' as const,
      fetchedAt: now,
      dataAsOf: v.generatedAt,
      stale: false,
    },
  ];

  async function kpis({ now, r, cr, t, c }: RealKpiInput) {
    const [vsl, vslC] = await Promise.all([
      ctx.loadVsl(r.from, r.to),
      cr ? ctx.loadVsl(cr.from, cr.to) : Promise.resolve(null),
    ]);
    const vm = vsl?.configured ? vsl.main : null;
    const vc = vslC?.configured ? vslC.main : null;
    const out: Partial<Record<KpiId, KpiValue>> = {};
    if (!vm || !vsl) return out;
    const vfresh = vfreshOf(now, vsl);
    const add = (id: KpiId, a: number | null, b: number | null) => {
      out[id] = { ...kv(id, a, b, now), freshness: vfresh };
    };
    add('vslViews', vm.stats.views, vc?.stats.views ?? null);
    add('vslPlayRate', vm.stats.playRate, vc?.stats.playRate ?? null);
    add('vslEngagement', vm.stats.engagement, vc?.stats.engagement ?? null);
    add('pitchRetention', vm.stats.pitchRetention, vc?.stats.pitchRetention ?? null);
    add(
      'vslPageConv',
      vm.stats.views ? t.front / vm.stats.views : null,
      vc?.stats.views && c ? c.front / vc.stats.views : null,
    );
    if (vsl.live != null) add('vslLive', vsl.live, null);
    return out;
  }

  /** Grava os números da V2 da VTurb em `out` (só os pedidos em `ids`). */
  function writeV2(
    ids: readonly KpiIdV2[],
    out: Partial<Record<KpiIdV2, KpiValueV2>>,
    sim: Partial<Record<KpiIdV2, KpiValueV2>>,
    v: RealVsl,
    frontSales: number,
    now: number,
  ) {
    const vals = vslV2Values(v.main, v.upsell, frontSales);
    for (const id of VSL_KPI_V2_IDS) {
      const x = vals[id];
      if (!x || !ids.includes(id)) continue;
      // Objeto novo (nada herdado da simulação: nem minigráfico, nem meta, nem nota).
      out[id] = {
        id,
        value: x.value,
        compareValue: null,
        delta: null,
        deltaKind: sim[id]?.deltaKind ?? 'pp',
        status: x.status,
        freshness: vfreshOf(now, v) as KpiValueV2['freshness'],
        ...(x.sample ? { sample: x.sample } : {}),
      } as KpiValueV2;
    }
  }

  /** Snapshot: os 3 números da V2 calculados com a VTurb (o resto da simulação sai no núcleo). */
  async function patchSnapshot(q: Query, snap: DashboardSnapshot, real: RealKpisResult) {
    if (!snap.kpisV2) return;
    const now = clock();
    const r = rangeMs(q, now);
    const v = vslOk(await ctx.loadVsl(r.from, r.to));
    if (!v) return;
    writeV2(VSL_KPI_V2_IDS, snap.kpisV2, { ...snap.kpisV2 }, v, real.t.front, now);
  }

  async function patchKpisV2(
    q: Parameters<NonNullable<PainelProviderV2['getKpisV2']>>[0],
    out: Partial<Record<KpiIdV2, KpiValueV2>>,
    sim: Partial<Record<KpiIdV2, KpiValueV2>>,
  ) {
    if (!VSL_KPI_V2_IDS.some((id) => q.ids.includes(id))) return;
    const now = clock();
    const r = rangeMs(q, now);
    const v = vslOk(await ctx.loadVsl(r.from, r.to));
    if (!v) return;
    const rows = await ctx.load(r.from, r.to).catch(() => [] as RealOrderRow[]);
    const front = rows.filter((o) => isPaid(o) && o.kind === 'front' && matches(o, q)).length;
    writeV2(q.ids, out, sim, v, front, now);
  }

  async function getRetention(q: RetentionQuery) {
    const now = clock();
    const r = rangeMs(q, now);
    const v = await ctx.loadVsl(r.from, r.to);
    // Sem VTurb (ou com erro) a curva não existe: nada da simulação.
    if (!v?.configured) throw vturbUnavailable('VTurb não conectada');
    if (v.error) throw vturbUnavailable(v.error);
    const p = q.playerRole === 'upsell' ? v.upsell : v.main;
    if (!p) return { curves: [], milestones: [], abTest: null };
    const all = toCurve(p, now);
    // Por criativo e por dispositivo a VTurb pede uma consulta por grupo: por enquanto, só a curva geral.
    return { curves: q.segmentBy === 'all' ? [all] : [], milestones: milestones(all), abTest: null };
  }

  async function listVslSources(q: ListQuery): Promise<Page<unknown>> {
    const now = clock();
    const r = rangeMs(q, now);
    const [v, ins] = await Promise.all([ctx.loadVsl(r.from, r.to), ctx.loadInsights(r.from, r.to, 'ad')]);
    // Sem VTurb, nenhuma origem (nada da simulação); com erro, o erro da fonte.
    if (!v?.configured) return paginate([], q);
    if (v.error) throw vturbUnavailable(v.error);
    const names = new Map((ins?.rows ?? []).map((x) => [x.id, x.name]));
    return paginate(v.main ? toSourceRows(v.main, names) : [], q);
  }

  /**
   * Duração e momento da oferta dos players reais (VTurb) no lugar dos da simulação, marcados com a
   * origem (`source: 'vturb'`), com `blocksSaved` (os blocos da copy foram cadastrados nos Ajustes) e
   * com `pitchKnown` (há um momento de pitch real: da VTurb ou salvo nos Ajustes).
   */
  async function withVslPlayers<B extends { settings: Settings }>(boot: B): Promise<B> {
    const b = boot as B & {
      players?: { role: string; durationSec: number; pitchSec: number; name: string; id: string }[];
    };
    if (!b.players) return boot;
    const v = vslOk(await ctx.loadVsl(dayStart(todayKey(clock())), clock()));
    if (!v) return boot;
    const real = { vsl_main: v.main, upsell: v.upsell } as Record<string, RealVsl['main']>;
    if (!b.players.some((p) => (real[p.role]?.durationSec ?? 0) > 0)) return boot;
    const saved = await savedVsl().catch(() => NOTHING_SAVED);
    return {
      ...boot,
      players: b.players.map((p) => {
        const r = real[p.role];
        if (!r || r.durationSec <= 0) return p;
        // Pitch: o da VTurb; sem ele, o que o dono salvou nos Ajustes; sem nenhum, desconhecido (0 e
        // `pitchKnown: false`). Nunca o do boot: é o da simulação.
        const own = p.role === 'upsell' ? saved.upsellPitchSec : saved.pitchSec;
        const pitchSec = r.pitchSec > 0 ? r.pitchSec : (own ?? 0);
        return {
          ...p,
          id: r.playerId,
          name: r.name,
          durationSec: r.durationSec,
          pitchSec,
          source: 'vturb',
          blocksSaved: saved.blocks,
          pitchKnown: pitchSec > 0,
        };
      }),
    };
  }

  return {
    kpis,
    kpiIds: VSL_KPI_IDS,
    neverSimKpis: VSL_KPI_IDS,
    overrides: { getRetention },
    patchSnapshot,
    patchKpisV2,
    list: (kind, q) => (kind === 'vslSources' ? listVslSources(q) : undefined),
    patchBoot: withVslPlayers,
  };
}
